import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";

import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import Textarea from "../../../../../Components/TextArea";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import TextInput from "../../../../../Components/TextInput";
import CustomTableLoader from "../../../../../Components/CustomTableLoader";
import TableActionLoader from "../../../../../Components/TableActionLoader";
import FileActionLoader from "../../../../../Components/FileActionLoader";
import GoBackButton from "../../../../../Components/GoBackButton";
import { documentFileExtension, imageFileExtension } from "../../../../../Api/FileConstant";
import { goToTop } from "../../../../../Components/goToTop";
import { checkText } from "../../../../../Utils/Validator";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { formatDate } from "../../../../../Utils/getDate";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";

import {
    SiteVisitReport as SiteVisitReportType,
    SiteVisitReportNoteList,
    SiteVisitReportBuildingInspectorResponse,
    ResponseFileList,
    SiteVisitReportArchitectResponse,
    NoteStatus,
    noteStatuses,
} from "../../../../../Controllers/Types/SiteVisitReport/SiteVisitReportType";
import { fetchSiteVisitReports } from "../../../../../Controllers/Thunks/SiteVisitReportThunks";

import {
    createSiteVisitReportNote,
    deleteSiteVisitReportNote,
    updateSiteVisitReportNote,
    deleteSiteVisitReportNoteFile,
    siteVisitReportNoteResponseByArchitect,
    deleteSiteVisitReportNoteArchitectResponse,
    deleteSiteVisitReportNoteArchitectResponseFile,
    sendSiteVisitReportViaEmail
} from "../../../../../Api/SiteVisitReport/SiteVisitReportApiServices";
import ImagePreview from "../../../../../Components/ImagePreview";

interface EmailInput {
    value: string;
    error: string;
}

const SiteVisitReportDetail: React.FC = () => {
    const dispatch = useAppDispatch();
    const today = new Date();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useRef<Toast>(null);
    const projectData = location.state?.projectData;
    const folderId = Number(projectData.folder_id);

    /* Check role */
    const [isInspector, setIsInspector] = useState<boolean>(false);
    const [isArchitect, setIsArchitect] = useState<boolean>(false);

    useEffect(() => {
        const checkRole = async () => {
            const [inspectorResult, architectResult] = await Promise.all([
                checkIsBuildingInspector(),
                checkIsArchitect(),
            ]);

            setIsInspector(inspectorResult);
            setIsArchitect(architectResult);
        };

        checkRole();
    }, []);
    /*  */

    const [loading, setLoading] = useState<boolean>(false);
    const [saveNextLoading, setSaveNextLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
    const [noteFileDeleteLoadingId, setNoteFileDeleteLoadingId] = useState<number | null>(null);
    const [responseFileDeleteLoadingId, setResponseFileDeleteLoadingId] = useState<number | null>(null);
    const [isSignedReportByBi, setIsSignedReportByBi] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    const {
        siteVisitReportLoading, siteVisitReportError, siteVisitReports
    } = useAppSelector((state) => state?.siteVisitReport);

    const [siteVisitReportData, setSiteVisitReportData] = useState<SiteVisitReportType | null>(null);
    const [siteVisitReportNotesData, setSiteVisitReportNotesData] = useState<SiteVisitReportNoteList[]>([]);

    /* Notes */
    const [selectedNoteData, setSelectedNoteData] = useState<SiteVisitReportNoteList | null>(null);
    const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
    const [noteNumber, setNoteNumber] = useState<string>("");
    const [note, setNote] = useState<string>('');
    const [noteStatus, setNoteStatus] = useState<NoteStatus | null>(noteStatuses[1]);
    const [noteFiles, setNoteFiles] = useState<File[]>([]);

    const [noteNumberError, setNoteNumberError] = useState<string>("");
    const [noteError, setNoteError] = useState<string>('');
    const [noteStatusError, setNoteStatusError] = useState<string>('');
    const [noteFilesError, setNoteFilesError] = useState<string>('');
    const [noteUpdateHasChanges, setNoteUpdateHasChanges] = useState<boolean>(false);

    /* For architect response */
    const [showArchitectResponseModal, setShowArchitectResponseModal] = useState<boolean>(false);
    const [arcResponseNote, setArcResponseNote] = useState<string>('');
    const [arcResponseFiles, setArcResponseFiles] = useState<File[]>([]);

    const [arcResponseNoteError, setArcResponseNoteError] = useState<string>('');
    const [arcResponseFilesError, setArcResponseFilesError] = useState<string>('');
    const [arcResponseHasChanges, setArcResponseHasChanges] = useState<boolean>(false);

    const [documentFilePath, setDocumentFilePath] = useState<string>("");
    const [showNoteDocViewModal, setShowNoteDocViewModal] = useState<boolean>(false);

    /* For send email */
    const [showMailModal, setShowMailModal] = useState<boolean>(false);
    const [emailAddresses, setEmailAddresses] = useState<EmailInput[]>([
        { value: '', error: '' },
    ]);

    /* PERMISION HANDLER */
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const havePermissionDownloadSiteVisitReport = useCanEdit("Site Visit Report", "download_site_visit_report");
    const havePermissionSendSiteVisitReport = useCanEdit("Site Visit Report", "share_site_visit_report");
    const havePermissionAddSiteVisitReportQuestion = useCanEdit("Site Visit Report", "add_site_visit_report_question");
    const havePermissionAddSiteVisitReportNote = useCanEdit("Site Visit Report", "add_site_visit_report_note");
    const havePermissionEditSiteVisitReportNote = useCanEdit("Site Visit Report", "edit_site_visit_report_note");
    const havePermissionDeleteSiteVisitReportNote = useCanEdit("Site Visit Report", "delete_site_visit_report_note");
    const havePermissionSiteVisitReportResponse = useCanEdit("Site Visit Report", "site_visit_report_response");
    const havePermissionDeleteSiteVisitReportResponse = useCanEdit("Site Visit Report", "delete_site_visit_report_response");
    /*  */

    // Fetch data initially from state
    useEffect(() => {
        siteVisitReportDataGet();
    }, [folderId]);

    const siteVisitReportDataGet = async () => {
        await dispatch(fetchSiteVisitReports(folderId));
        setInitialDataLoading(false);
    }

    useEffect(() => {
        if (location?.state && location?.state.siteVisitReportData) {
            const hasData = siteVisitReports && siteVisitReports.length > 0;
            const stateData = location?.state?.siteVisitReportData as SiteVisitReportType;

            if (hasData) {
                const data = siteVisitReports.filter((siteVisitReport) => siteVisitReport.id === stateData.id);

                if (data && data.length > 0) {
                    setSiteVisitReportData(data[0]);
                } else {
                    setSiteVisitReportData(null);
                }
            } else {
                setSiteVisitReportData(null);
            }
        }
    }, [location?.state, siteVisitReports]);


    useEffect(() => {
        if (siteVisitReportData && siteVisitReportData !== null) {
            if (siteVisitReportData?.sign_report === 1) {
                setIsSignedReportByBi(true);
            } else {
                setIsSignedReportByBi(false);
            }
        } else {
            setIsSignedReportByBi(false);
        }
    }, [siteVisitReportData]);

    useEffect(() => {
        if (!siteVisitReportData || siteVisitReportData?.note_list?.length === 0) {
            setSiteVisitReportNotesData([]);
            return;
        }

        setSiteVisitReportNotesData(siteVisitReportData?.note_list ?? []);
    }, [siteVisitReportData]);
    /* *************************************************************************** */


    /* ********************************* Notes *********************************** */
    const handleCloseNoteModal = () => {
        setShowNoteModal(false);

        handleResetNoteFields();
        handleResetNoteErrors();
    }

    const handleResetNoteFields = () => {
        setSelectedNoteData(null);
        setNote('');
        setNoteFiles([]);
        setDataState('Add');
        setNoteStatus(noteStatuses[1]);
    }

    const handleResetNoteErrors = () => {
        setNoteError('');
        setNoteFilesError('');
        setNoteStatusError('');
        setNoteNumberError('');
    }

    const handleNoteFilesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setNoteFiles((prev) => [...prev, ...filesArray]);
        }
    }

    const handleRemoveNoteFile = (index: number) => {
        setNoteFiles((prev) => prev.filter((_, i) => i !== index));
    }

    useEffect(() => {
        if (dataState === 'Add' && siteVisitReportData !== null) {
            generateNoteNumber();
        }
    }, [dataState, siteVisitReportData]);

    const generateNoteNumber = () => {
        let noteNo = 0;
        noteNo = siteVisitReportData?.note_list?.length! + 1;

        setNoteNumber(noteNo.toString());
    }

    // validations for note.
    function checkCredentialsForNote(): boolean {
        const note_number = checkText(noteNumber, true, setNoteNumberError);
        const inspection_note = checkText(note, true, setNoteError);

        return note_number && inspection_note;
    }

    // clear message
    useEffect(() => {
        checkText(noteNumber, false, setNoteNumberError);
    }, [noteNumber]);

    useEffect(() => {
        checkText(note, false, setNoteError);
    }, [note]);

    useEffect(() => {
        if (noteStatus) {
            setNoteStatusError('');
        }
    }, [noteStatus]);

    useEffect(() => {
        if (noteFiles) {
            setNoteFilesError('');
        }
    }, [noteFiles]);

    // handle api error
    function showAPIErrorForNote(keyName: string, error: string): void {
        if (keyName === "note_no") {
            setNoteNumberError(error);
        }

        if (keyName === "note") {
            setNoteError(error);
        }

        if (keyName === "status") {
            setNoteStatusError(error);
        }

        if (keyName === "file_list[]") {
            setNoteFilesError(error);
        }
    }

    const handleAddNote = () => {
        setShowNoteModal(true);
        setDataState('Add');
        generateNoteNumber();
    }

    const handleCreateNote = async (e: React.FormEvent, saveState: 'Close' | 'Next') => {
        e.preventDefault();

        if (!checkCredentialsForNote() || !siteVisitReportData) return;

        saveState === 'Next'
            ? setSaveNextLoading(true)
            : setLoading(true);

        try {
            const site_visit_id: number = siteVisitReportData?.id;
            const note_no: string = noteNumber ?? '';
            const inspection_note: string = note ?? '';
            const status: string = noteStatus?.value ?? '';
            const file_list: File[] = noteFiles ?? [];

            const data = await createSiteVisitReportNote(
                site_visit_id,
                note_no,
                inspection_note,
                status,
                file_list
            )

            if (data?.statusCode === 200) {
                siteVisitReportDataGet();

                if (saveState === 'Close') {
                    handleCloseNoteModal();
                } else {
                    handleResetNoteFields();
                    handleResetNoteErrors();
                }

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Note added successfully.',
                    life: 3000
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForNote(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error creating the note.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error creating the note:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while creating the note.",
                life: 3000,
            });
        } finally {
            saveState === 'Next'
                ? setSaveNextLoading(false)
                : setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedNoteData && selectedNoteData !== null) {
            if (note !== selectedNoteData?.note) {
                setNoteUpdateHasChanges(true);
            } else if (noteStatus?.value !== selectedNoteData?.status) {
                setNoteUpdateHasChanges(true);
            } else if (noteFiles && noteFiles?.length !== 0) {
                setNoteUpdateHasChanges(true);
            } else {
                setNoteUpdateHasChanges(false);
            }
        }
    }, [selectedNoteData, note, noteStatus, noteFiles]);

    const handleEditNote = (note: SiteVisitReportNoteList) => {
        if (!note) return;

        setShowNoteModal(true);
        setDataState('Edit');

        const status = noteStatuses?.find(s => s.value === note?.status)

        setNote(note?.note || '');
        setNoteStatus(status ?? null);
        setNoteNumber(String(note?.note_no));

        setSelectedNoteData(note);
    }

    const handleUpdateNote = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsForNote() || !siteVisitReportData || !selectedNoteData) return;

        setLoading(true);

        try {
            const note_id: number = selectedNoteData?.id;
            const site_visit_id: number = siteVisitReportData?.id;
            const inspection_note: string = note ?? '';
            const status: string = noteStatus?.value ?? '';
            const file_list: File[] = noteFiles ?? [];

            const data = await updateSiteVisitReportNote(
                note_id,
                site_visit_id,
                inspection_note,
                status,
                file_list
            )

            if (data?.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseNoteModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Note updated successfully.',
                    life: 3000
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForNote(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating the note.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating the note:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating the note.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteNote = (): void => {
        if (!selectedNoteData) return;

        confirmDialog({
            message: 'Are you sure you want to delete this note?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteNote(selectedNoteData?.id!),
        });
    }

    const deleteNote = async (noteId: number) => {
        if (!noteId) return;

        setDeleteLoading(true);

        try {
            const result = await deleteSiteVisitReportNote(noteId);

            if (result.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseNoteModal();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Note deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the note.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the note:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the note.",
                life: 3000,
            });
        } finally {
            setDeleteLoading(false);
        }
    }

    // update the state after file delete
    useEffect(() => {
        if (siteVisitReportData && siteVisitReportData !== null) {
            if (dataState === 'Edit' && selectedNoteData && selectedNoteData !== null) {
                const updatedNoteData = siteVisitReportData?.note_list?.find(note => note?.id === selectedNoteData?.id);

                setSelectedNoteData(updatedNoteData || null);
            }
        }

    }, [dataState, selectedNoteData, siteVisitReportData]);

    const handleDeleteNoteFile = (noteFileId: number) => {
        if (!noteFileId) return;

        confirmDialog({
            message: 'Are you sure you want to delete this uploaded file?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteNoteFile(noteFileId),
        });
    }

    const deleteNoteFile = async (noteFileId: number) => {
        if (!noteFileId) return;

        setNoteFileDeleteLoadingId(noteFileId);

        try {
            const result = await deleteSiteVisitReportNoteFile(noteFileId);

            if (result.statusCode === 200) {
                siteVisitReportDataGet();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the file.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the file:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the file.",
                life: 3000,
            });
        } finally {
            setNoteFileDeleteLoadingId(null);
        }
    }

    const noteModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} note
            </h5>

            {dataState === 'Edit' ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <React.Fragment>
                            {havePermissionDeleteSiteVisitReportNote && (
                                <button type="button" className="close_modal_btn p-ripple"
                                    onClick={handleDeleteNote}>
                                    <i className="bi bi-trash3"></i>
                                    <Ripple />
                                </button>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <button type="button" className="close_modal_btn p-ripple"
                    onClick={handleCloseNoteModal}>
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    )

    const noteModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseNoteModal}
            />

            {dataState === 'Add' ? (
                <React.Fragment>
                    <Button
                        loading={loading}
                        label={`${loading ? 'Processing' : 'Save & Close'}`}
                        className="custom_btn primary_light"
                        onClick={(e) => handleCreateNote(e, 'Close')}
                    />

                    <Button
                        loading={saveNextLoading}
                        label={`${saveNextLoading ? 'Processing' : 'Save & Next'}`}
                        className="custom_btn primary"
                        onClick={(e) => handleCreateNote(e, 'Next')}
                    />
                </React.Fragment>
            ) : (
                <Button
                    loading={loading}
                    label={`${loading ? 'Processing' : 'Update'}`}
                    className="custom_btn primary"
                    onClick={handleUpdateNote}
                    disabled={dataState === 'Edit' && !noteUpdateHasChanges}
                />
            )}
        </div>
    )
    /* *************************************************************************** */


    /* *************************** Architect response **************************** */
    const handeleCloseArcResponseModal = (mode: 'Next' | 'Close') => {
        if (mode === 'Close') {
            setShowArchitectResponseModal(false);
            setSelectedNoteData(null);
        }

        handleResetArcResponseFields();
        handleResetArcResponseErrors();
        setDataState('Add');
    }

    const handleResetArcResponseFields = () => {
        setArcResponseNote('');
        setArcResponseFiles([]);
    }

    const handleResetArcResponseErrors = () => {
        setArcResponseNoteError('');
        setArcResponseFilesError('');
    }

    // validations for response.
    function checkCredentialsArcResponse(): boolean {
        const response_note = checkText(arcResponseNote, true, setArcResponseNoteError);

        return response_note;
    }

    // clear message
    useEffect(() => {
        checkText(arcResponseNote, false, setArcResponseNoteError);
    }, [arcResponseNote]);

    // handle api error
    function showAPIErrorArcResponse(keyName: string, error: string): void {
        if (keyName === "response") {
            setArcResponseNoteError(error);
        }
    }

    /* Add */
    const handleAddArcResponse = (data: SiteVisitReportNoteList) => {
        if (!data) return;

        setDataState('Add');
        setShowArchitectResponseModal(true);
        setSelectedNoteData(data);

        handleResetArcResponseFields();
        handleResetArcResponseErrors();
    }

    /* Create */
    const handleResponseArchitect = async (e: React.FormEvent, responseState: 'Next' | 'Close') => {
        e.preventDefault();

        if (!checkCredentialsArcResponse() || !siteVisitReportData || !selectedNoteData) return;

        const isNext = responseState === 'Next';
        isNext ? setSaveNextLoading(true) : setLoading(true);

        try {
            const site_visit_id: number = siteVisitReportData?.id!;
            const note_id: number = selectedNoteData?.id!;
            const note_response: string = arcResponseNote!;
            const date: string = formatDate(today!);
            const file_list: File[] = arcResponseFiles || [];

            const data = await siteVisitReportNoteResponseByArchitect(
                site_visit_id,
                note_id,
                note_response,
                date,
                file_list
            )

            if (data?.statusCode === 200) {
                siteVisitReportDataGet();

                if (isNext) {
                    const nextItem = siteVisitReportData.note_list?.find(
                        (note) =>
                            note?.id !== selectedNoteData.id &&
                            note?.id! > selectedNoteData.id &&
                            note?.response_architect === null &&
                            note?.status !== 'Satisfactory'
                    );

                    handeleCloseArcResponseModal('Next');

                    if (nextItem) {
                        setSelectedNoteData(nextItem);
                    }
                } else {
                    handeleCloseArcResponseModal('Close');
                }

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Response ${dataState === 'Add' ? 'saved' : 'updated'} successfully.`,
                    life: 3000
                });
            } else if (data?.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorArcResponse(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: `There was an error ${dataState === 'Add' ? 'creating' : 'updating'} the response.`,
                    life: 3000
                });
            }
        } catch (error) {
            console.error(`Error ${dataState === 'Add' ? 'creating' : 'updating'} the response:`, error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: `Something went wrong while ${dataState === 'Add' ? 'creating' : 'updating'} the response.`,
                life: 3000,
            });
        } finally {
            isNext ? setSaveNextLoading(false) : setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedNoteData && selectedNoteData !== null) {
            if (arcResponseNote !== selectedNoteData?.response_architect?.response) {
                setArcResponseHasChanges(true);
            } else if (arcResponseFiles && arcResponseFiles?.length !== 0) {
                setArcResponseHasChanges(true);
            } else {
                setArcResponseHasChanges(false);
            }
        }
    }, [selectedNoteData, arcResponseNote, arcResponseFiles]);

    /* Edit */
    const handleEditArcResponse = (data: SiteVisitReportNoteList) => {
        if (!data) return;

        setDataState('Edit');
        setShowArchitectResponseModal(true);
        setSelectedNoteData(data);
        setArcResponseNote(data?.response_architect?.response!);
    }

    const handleDeleteResponse = (): void => {
        if (!selectedNoteData) return;

        confirmDialog({
            message: 'Are you sure you want to delete this response?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteResponse(selectedNoteData?.response_architect?.id ?? null),
        });
    }

    const deleteResponse = async (responseId: number | null) => {
        if (!responseId) return;

        setDeleteLoading(true);

        try {
            const result = await deleteSiteVisitReportNoteArchitectResponse(responseId);

            if (result.statusCode === 200) {
                siteVisitReportDataGet();
                handeleCloseArcResponseModal('Close');

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Response deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the response.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the response:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the response.",
                life: 3000,
            });
        } finally {
            setDeleteLoading(false);
        }
    }

    const responseModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState} Response
            </h5>

            {dataState === 'Edit' ? (
                <React.Fragment>
                    {deleteLoading ? (
                        <TableActionLoader className="is_modal_loader" />
                    ) : (
                        <React.Fragment>
                            {havePermissionDeleteSiteVisitReportResponse && (
                                <button type="button" className="close_modal_btn p-ripple"
                                    onClick={handleDeleteResponse}>
                                    <i className="bi bi-trash3"></i>
                                    <Ripple />
                                </button>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            ) : (
                <button
                    type="button"
                    className="close_modal_btn p-ripple"
                    onClick={() => handeleCloseArcResponseModal('Close')}
                >
                    <i className="bi bi-x-circle"></i>
                    <Ripple />
                </button>
            )}
        </div>
    )

    const responseModalFooter = () => {
        const hasMoreData =
            !!siteVisitReportData &&
            !!selectedNoteData &&
            (siteVisitReportData.note_list?.some((note) =>
                note?.id !== selectedNoteData?.id &&
                note?.id! > selectedNoteData?.id! &&
                note?.response_architect === null &&
                note?.status !== 'Satisfactory'
            ) ?? false);

        return (
            <div className="custom_modal_footer">
                <Button
                    label="Cancel"
                    className="custom_btn secondary"
                    onClick={() => handeleCloseArcResponseModal('Close')}
                />

                {dataState === 'Add' ? (
                    <>
                        <Button
                            label={`${loading ? 'Processing' : 'Save & Close'}`}
                            onClick={(e) => handleResponseArchitect(e, 'Close')}
                            className={`custom_btn ${hasMoreData ? 'primary_light' : 'primary'}`}
                            loading={loading}
                        />

                        {hasMoreData && (
                            <Button
                                label={saveNextLoading ? 'Processing' : 'Save & Next'}
                                onClick={(e) => handleResponseArchitect(e, 'Next')}
                                loading={saveNextLoading}
                                className="custom_btn primary"
                            />
                        )}
                    </>
                ) : (
                    <Button
                        loading={loading}
                        label={`${loading ? 'Processing' : 'Update'}`}
                        className="custom_btn primary"
                        onClick={(e) => handleResponseArchitect(e, 'Close')}
                        disabled={dataState === 'Edit' && !arcResponseHasChanges}
                    />
                )}
            </div>
        )
    }

    /* For response files */
    const handleResponseFilesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) {
            const filesArray = Array.from(event.target.files);
            setArcResponseFiles((prev) => [...prev, ...filesArray]);
        }
    }

    const handleRemoveResponseFile = (index: number) => {
        setArcResponseFiles((prev) => prev.filter((_, i) => i !== index));
    }

    const handleDeleteResponseFile = (resFileId: number) => {
        if (!resFileId) return;

        confirmDialog({
            message: 'Are you sure you want to delete this uploaded file?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            dismissableMask: true,
            resizable: false,
            draggable: false,
            accept: () => deleteResponseFile(resFileId),
        });
    }

    const deleteResponseFile = async (resFileId: number) => {
        if (!resFileId) return;

        setResponseFileDeleteLoadingId(resFileId);

        try {
            const result = await deleteSiteVisitReportNoteArchitectResponseFile(resFileId);

            if (result.statusCode === 200) {
                siteVisitReportDataGet();

                toast?.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File deleted successfully.',
                    life: 3000
                });
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error deleting the file.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error deleting the file:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while deleting the file.",
                life: 3000,
            });
        } finally {
            setResponseFileDeleteLoadingId(null);
        }
    }
    /* *************************************************************************** */


    /* ************************* Site visit detail common ************************* */
    const handlePreviewReport = () => {
        navigate(`/project/detail/default-service/onsite/site-visit-report/preview`,
            { state: { projectData: projectData, siteVisitReportData: siteVisitReportData } }
        );
        goToTop();
    }
    /* *************************************************************************** */


    /* ******************************** Download ********************************* */
    const handleDownloadReport = async () => {
        if (isDownloading) return;
        // const reportPath = siteVisitReportData?.file_download_path;
        const reportPath = siteVisitReportData?.file_download_path;
        if (!reportPath) {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "File not found!",
                    life: 3000,
                });
            }
            return;
        }

        setIsDownloading(true);

        setTimeout(async () => {
            try {
                const response = await fetch(reportPath);

                if (!response.ok) {
                    throw new Error("Failed to fetch the file.");
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = reportPath.split("/").pop() || "Site visit report.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);

                if (toast.current) {
                    toast.current.show({
                        severity: "success",
                        summary: "Success",
                        detail: "File downloaded successfully.",
                        life: 3000,
                    });
                }
            } catch (error) {
                if (toast.current) {
                    toast.current.show({
                        severity: "error",
                        summary: "Failed",
                        detail: "Error downloading the file",
                        life: 3000,
                    });
                }
            } finally {
                setIsDownloading(false);
            }
        }, 500);
    };

    const handleViewReportDocument = () => {
        if (siteVisitReportData?.file_download_path) {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(siteVisitReportData?.file_download_path);
        }
    };
    /* *************************************************************************** */



    /* *********************************** Mail ********************************** */
    const handleCloseMailModal = () => {
        setShowMailModal(false);
        setEmailAddresses([{ value: '', error: '' }]);
    }

    const handleSendEmail = () => {
        setShowMailModal(true);
    }

    const validateEmail = (email: string): string => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) return 'Email is required.';
        if (!emailRegex.test(email)) return 'Enter a valid email.';
        return '';
    };

    // validations.
    function checkCredentialsEmail(): boolean {
        const updatedEmails = emailAddresses.map((email) => {
            const error = validateEmail(email.value);
            return { ...email, error };
        });

        setEmailAddresses(updatedEmails);

        const hasErrors = updatedEmails.some(email => email.error !== '');
        return !hasErrors;
    }

    function showAPIErrorEmail(keyName: string, error: string): void {
        if (keyName === "email_list[]") {
            const updatedEmails = emailAddresses.map((email) => ({
                ...email,
                error,
            }));
            setEmailAddresses(updatedEmails);
        }
    }

    const sendEMail = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCredentialsEmail()) return;

        setLoading(true);

        try {
            const site_visit_id: number = siteVisitReportData?.id!;
            const email_list: string[] = emailAddresses.map((email) => email.value);

            const data = await sendSiteVisitReportViaEmail(
                site_visit_id,
                email_list
            );

            if (data.statusCode === 200) {
                siteVisitReportDataGet();
                handleCloseMailModal();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Email sent successfully.",
                    life: 3000,
                });
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIErrorEmail(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: "error",
                    summary: "Failed",
                    detail: "There was an error sending the email.",
                    life: 3000,
                });
            }
        } catch (error) {
            console.error("Error sending the email:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while sending the email.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }

    const handleAddMail = () => {
        const lastEmail = emailAddresses[emailAddresses.length - 1];
        const validationMessage = validateEmail(lastEmail.value);

        if (validationMessage) {
            const updatedEmails = [...emailAddresses];
            updatedEmails[updatedEmails.length - 1].error = validationMessage;
            setEmailAddresses(updatedEmails);
            return;
        }

        setEmailAddresses([...emailAddresses, { value: '', error: '' }]);
    };

    const handleRemoveMail = (index: number) => {
        if (emailAddresses.length === 1) return;
        const updatedEmails = emailAddresses.filter((_, i) => i !== index);
        setEmailAddresses(updatedEmails);
    };

    const handleEmailChange = (index: number, newValue: string) => {
        const updatedEmails = [...emailAddresses];
        updatedEmails[index].value = newValue;
        updatedEmails[index].error = '';
        setEmailAddresses(updatedEmails);
    };

    const mailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Send report via mail
            </h5>
            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseMailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    const mailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Cancel"
                className="custom_btn secondary"
                onClick={handleCloseMailModal}
            />
            <Button
                loading={loading}
                label={`${loading ? 'Sending' : 'Send'}`}
                className="custom_btn primary"
                onClick={sendEMail}
            />
        </div>
    )
    /* *************************************************************************** */

    const handleCloseFileViewModal = () => {
        setShowNoteDocViewModal(false);
    };

    const handleViewDocument = (filePath: string) => {
        if (filePath && filePath !== "") {
            setShowNoteDocViewModal(true);
            setDocumentFilePath(filePath);
        } else {
            setDocumentFilePath("");
        }
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content full_screen_view">
                {siteVisitReportLoading && initialDataLoading ? (
                    <div className="loading_container">
                        <ProgressSpinner className="progresscircle custom" style={{ width: '50px', height: '50px', stroke: '#8953D7' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration="1s" />
                    </div>
                ) : siteVisitReportError ? (
                    <ErrorPage errorMessage={siteVisitReportError} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>

                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>

                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.feature_name}
                                </li>

                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name} {siteVisitReportData && siteVisitReportData?.site_visit_no}
                                </li>
                            </ul>

                            <GoBackButton />
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="status_lable_container">
                                {noteStatuses?.length > 0 && (
                                    <div className="status_lable_area">
                                        {noteStatuses?.map((status, index) => (
                                            <div key={index} className="status_lable">
                                                <span style={{ backgroundColor: status.colorCode }}></span>
                                                <h6>{status.value}</h6>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="page_action_area">
                                {havePermissionAddSiteVisitReportNote && isSignedReportByBi === false && isInspector && (
                                    <button
                                        className="add_data_link_btn p-ripple"
                                        onClick={handleAddNote}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add note</span>
                                        <Ripple />
                                    </button>
                                )}

                                {siteVisitReportData?.file_download_path !== null && (
                                    <React.Fragment>
                                        {havePermissionDownloadSiteVisitReport && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleDownloadReport}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <ProgressSpinner
                                                        style={{ width: "20px", height: "20px" }}
                                                        strokeWidth="5"
                                                        fill="none"
                                                        animationDuration="1s"
                                                        className="spin_loader"
                                                    />
                                                ) : (
                                                    <i className="bi bi-download"></i>
                                                )}
                                                <span>{isDownloading ? "Downloading" : "Download"}</span>
                                                <Ripple />
                                            </button>
                                        )}

                                        {havePermissionSendSiteVisitReport && (
                                            <button
                                                className="add_data_link_btn p-ripple"
                                                onClick={handleSendEmail}
                                            >
                                                <i className="bi bi-envelope"></i>
                                                <span>Send</span>
                                                <Ripple />
                                            </button>
                                        )}
                                    </React.Fragment>
                                )}

                                <button
                                    className="add_data_link_btn p-ripple"
                                    onClick={handlePreviewReport}>
                                    <i className="bi bi-file-earmark-text"></i>
                                    <span>Preview report</span>
                                    <Ripple />
                                </button>
                            </div>
                        </div>

                        <div className="page_main_content_section no_header">
                            {siteVisitReportNotesData && siteVisitReportNotesData?.length > 0 ? (
                                <div className="pck_track_list_area">
                                    {siteVisitReportLoading && (
                                        <CustomTableLoader />
                                    )}

                                    <div className="table-responsive pck_track_list_table_area">
                                        <table className="table pck_track_list_table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "100%" }}>Inspection notes</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {siteVisitReportNotesData?.map((noteItem) => {
                                                    const status = noteItem?.status;
                                                    const statusColor = noteStatuses?.find(s => s?.value === status)?.colorStandard ?? '';

                                                    const biResponse: SiteVisitReportBuildingInspectorResponse | null = noteItem?.response_building_inspector || null;
                                                    const biResponseFiles: ResponseFileList[] = noteItem?.response_building_inspector?.file_list || [];

                                                    const arcResponse: SiteVisitReportArchitectResponse | null = noteItem?.response_architect || null;
                                                    const arcResponseFiles: ResponseFileList[] = noteItem?.response_architect?.file_list || [];

                                                    return (
                                                        <tr key={`${siteVisitReportData?.inspection_for}_item_${noteItem?.id}`} className="part_item_row">
                                                            <td style={{ width: "100%" }}>
                                                                <div className="item_list_wrapper">
                                                                    <div className={`pck_track_list_item_area with_indent ${statusColor}`}>
                                                                        <div className="pck_track_list_item_header">
                                                                            <span className="item_code">
                                                                                {noteItem?.note_no}.
                                                                            </span>

                                                                            <span className="item_cmt first_letter_capitalized">
                                                                                {noteItem?.note}
                                                                            </span>
                                                                        </div>

                                                                        {noteItem?.is_current_site_visit_note === 0 && (
                                                                            <h6 className={`last_data_info ${statusColor}`}>
                                                                                <i className="bi bi-info-circle text-dark me-1"></i>
                                                                                Reported during <span>Site visit {noteItem?.parent_site_visit_no}</span>
                                                                            </h6>
                                                                        )}

                                                                        {biResponse && biResponse !== null && (
                                                                            <React.Fragment>
                                                                                {biResponseFiles && biResponseFiles?.length > 0 && (
                                                                                    <div className="note_files_area">
                                                                                        <div className="checklist_img_container">
                                                                                            {biResponseFiles?.map((file, index) => (
                                                                                                <div
                                                                                                    className="note_image_container response_image_container"
                                                                                                    key={file?.id}
                                                                                                >
                                                                                                    {file?.extension === "pdf" ? (
                                                                                                        <div
                                                                                                            className="response_doc_file_container"
                                                                                                            onClick={() => handleViewDocument(file?.path!)}
                                                                                                        >
                                                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                                                        </div>
                                                                                                    ) : (
                                                                                                        <ImagePreview
                                                                                                            src={file?.path!}
                                                                                                            alt={`sv_report_image_${file?.id}`}
                                                                                                        />
                                                                                                    )}
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </React.Fragment>
                                                                        )}

                                                                        <div className="response_display_area custom_padding">
                                                                            {arcResponse && arcResponse !== null && (
                                                                                <div className="response_input_area mb-md-0 readonly">
                                                                                    <label>Architect Response :&nbsp;</label>

                                                                                    <p className="first_letter_capitalized">
                                                                                        {arcResponse?.response ? (
                                                                                            arcResponse?.response
                                                                                        ) : (
                                                                                            <span className="text_empty text_italic">N/A</span>
                                                                                        )}
                                                                                    </p>

                                                                                    {arcResponseFiles && arcResponseFiles?.length > 0 && (
                                                                                        <div className="response_file_area mt-3 pb-0">
                                                                                            <div className="checklist_img_container">
                                                                                                {arcResponseFiles?.map((file, index) => (
                                                                                                    <div
                                                                                                        className="note_image_container response_image_container"
                                                                                                        key={file?.id}
                                                                                                    >
                                                                                                        {file?.extension === "pdf" ? (
                                                                                                            <div
                                                                                                                className="response_doc_file_container"
                                                                                                                onClick={() => handleViewDocument(file?.path!)}
                                                                                                            >
                                                                                                                <i className="bi bi-file-earmark-pdf"></i>
                                                                                                            </div>
                                                                                                        ) : (
                                                                                                            <ImagePreview
                                                                                                                src={file?.path!}
                                                                                                                alt={`sv_report_image_${file?.id}`}
                                                                                                            />
                                                                                                        )}
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {isInspector ? (
                                                                            <React.Fragment>
                                                                                {isSignedReportByBi === false && havePermissionEditSiteVisitReportNote && (
                                                                                    <div className="pck_track_list_item_footer">
                                                                                        <div className="pck_track_list_item_btn_group">
                                                                                            <button
                                                                                                className={`table_action_btn white bordered p-ripple`}
                                                                                                onClick={() => handleEditNote(noteItem)}
                                                                                            >
                                                                                                Edit
                                                                                                <Ripple />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </React.Fragment>
                                                                        ) : isArchitect ? (
                                                                            <React.Fragment>
                                                                                {(isSignedReportByBi && status !== 'Satisfactory' && havePermissionSiteVisitReportResponse) && (
                                                                                    <div className="pck_track_list_item_footer">
                                                                                        <div className="pck_track_list_item_btn_group">
                                                                                            <button
                                                                                                className={`table_action_btn white ${arcResponse !== null ? 'success' : ''} bordered p-ripple`}
                                                                                                onClick={() => arcResponse === null ? handleAddArcResponse(noteItem) : handleEditArcResponse(noteItem)}
                                                                                            >
                                                                                                {arcResponse === null ? 'Response' : 'Responsed '}
                                                                                                <Ripple />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </React.Fragment>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <NoDataCreated message={`No note(s) has been created. ${isInspector ? 'To create the note click on add button.' : ''}`} />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Note modal */}
            <Dialog
                visible={showNoteModal}
                header={noteModalHeader}
                footer={noteModalFooter}
                headerClassName="custom_modal_header"
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseNoteModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-2 w-100">
                        <div className="row">
                            {/* Inspection */}
                            <div className="col-12">
                                <div className="page_form_group">
                                    <h6 className="data_label">
                                        <span className="text-dark"> Inspection for :</span>&nbsp;{siteVisitReportData?.inspection_for ?? ''}
                                    </h6>
                                </div>
                            </div>

                            {/* Item code */}
                            <div className="col-12 col-sm-6">
                                <div className="page_form_group">
                                    <label htmlFor="itemCode" className="custom_form_label">
                                        Note number&nbsp;
                                        <small>
                                            {dataState === "Add" ? "(Auto generated)" : "(Can't modify)"}
                                        </small>
                                    </label>

                                    <InputText
                                        key={`noteNumber`}
                                        id="noteNumber"
                                        value={String(noteNumber).padStart(2, '0')}
                                        className="custom_form_input"
                                        placeholder="Eg: 01"
                                        disabled
                                    />

                                    {noteNumberError && (
                                        <small className="form_error_msg">
                                            {noteNumberError}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Note */}
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="note" className="custom_form_label is_required">Note:</label>

                                    <InputTextarea
                                        id="note"
                                        value={note}
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNote(e.target.value)}
                                        className="custom_form_input is_text_area"
                                        placeholder="Note"
                                        autoComplete="off"
                                    />

                                    {noteError && <small className="form_error_msg">{noteError}</small>}
                                </div>
                            </div>

                            {/* Status */}
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="noteStatus" className="custom_form_label is_required">Status</label>

                                    <div className="response_status_container px-0">
                                        {noteStatuses?.map((status, index) => (
                                            <React.Fragment key={`noteStatus${index}`}>
                                                <Tooltip
                                                    target={`.note_status${index}`}
                                                />

                                                <div className="response_status_grp">
                                                    <RadioButton
                                                        inputId={status.value}
                                                        value={noteStatus}
                                                        onChange={() => setNoteStatus(status)}
                                                        checked={noteStatus === status}
                                                    />

                                                    <label htmlFor={status.value}
                                                    >
                                                        {status.lable}
                                                    </label>

                                                    <i
                                                        className={`bi bi-info-circle cursor_help text_secondary note_status${index}`}
                                                        data-pr-tooltip={status.value}
                                                        data-pr-position="top"
                                                    ></i>
                                                </div>
                                            </React.Fragment>
                                        ))}
                                    </div>

                                    {noteStatusError &&
                                        <small className="form_error_msg">{noteStatusError}</small>
                                    }
                                </div>
                            </div>

                            {/* Files */}
                            <div className="col-12">
                                <div className="page_form_group">
                                    <label htmlFor="noteFiles" className="custom_form_label">Files</label>

                                    <div className="checklist_img_container mt-2 mb-1">
                                        {dataState === 'Edit' && (
                                            <React.Fragment>
                                                {selectedNoteData?.response_building_inspector && selectedNoteData?.response_building_inspector !== null && (
                                                    <React.Fragment>
                                                        {selectedNoteData?.response_building_inspector?.file_list && selectedNoteData?.response_building_inspector?.file_list?.length > 0 && (
                                                            <React.Fragment>
                                                                {selectedNoteData?.response_building_inspector?.file_list?.map((file, index) => (
                                                                    <div className="note_image_container response_image_container" key={file?.id}>
                                                                        {noteFileDeleteLoadingId === file?.id ? (
                                                                            <FileActionLoader />
                                                                        ) : (
                                                                            <React.Fragment>
                                                                                {file?.extension === "pdf" ? (
                                                                                    <div
                                                                                        className="response_doc_file_container"
                                                                                        onClick={() => handleViewDocument(file?.path!)}
                                                                                    >
                                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                                    </div>
                                                                                ) : (
                                                                                    <ImagePreview
                                                                                        src={file?.path!}
                                                                                        alt={`sv_report_image_${file?.id}`}
                                                                                    />
                                                                                )}
                                                                            </React.Fragment>
                                                                        )}

                                                                        <button
                                                                            className="img_clear_btn p-ripple"
                                                                            onClick={() => handleDeleteNoteFile(file?.id!)}
                                                                        >
                                                                            <i className="bi bi-x"></i>
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </React.Fragment>
                                        )}

                                        {noteFiles && noteFiles?.length > 0 && (
                                            <React.Fragment>
                                                {noteFiles.map((file, index) => {
                                                    const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                    return (
                                                        <div
                                                            className="note_image_container response_image_container"
                                                            key={index}
                                                        >
                                                            {fileExtension === "pdf" ? (
                                                                <div
                                                                    className="response_doc_file_container"
                                                                    onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                >
                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                </div>
                                                            ) : (
                                                                <ImagePreview
                                                                    src={URL.createObjectURL(file)}
                                                                    alt={`sv_report_image_${index}`}
                                                                />
                                                            )}

                                                            <button
                                                                className="img_clear_btn p-ripple"
                                                                onClick={() => handleRemoveNoteFile(index)}
                                                            >
                                                                <i className="bi bi-x"></i>
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </React.Fragment>
                                        )}

                                        <Tooltip target={`.img_upload_label`} />

                                        <label
                                            htmlFor="noteFiles"
                                            className="img_upload_label p-ripple"
                                            data-pr-tooltip="Upload files"
                                            data-pr-position="right"
                                        >
                                            <i className="bi bi-upload"></i>
                                            <input
                                                id="noteFiles"
                                                multiple
                                                type="file"
                                                accept={documentFileExtension + ', ' + imageFileExtension}
                                                onChange={handleNoteFilesUpload}
                                            />
                                            <Ripple />
                                        </label>
                                    </div>

                                    <span className="file_upload_info pt-1">
                                        <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                    </span>

                                    {noteFilesError &&
                                        <small className="form_error_msg">{noteFilesError}</small>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Note response modal */}
            <Dialog
                visible={showArchitectResponseModal}
                headerClassName="custom_modal_header"
                header={responseModalHeader}
                footer={responseModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={() => handeleCloseArcResponseModal('Close')}>
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        {selectedNoteData && selectedNoteData !== null && (
                            <div className="row">
                                {/* Item */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="biNote" className={`custom_form_label`}>Inspection: </label>
                                        <div className="data_label flex-column align-items-start">
                                            <span className="text-capitalize text-dark pb-1 w-100">{siteVisitReportData?.inspection_for}:</span>
                                            <span className="w-100">
                                                <span className="text-secondary">{selectedNoteData?.note_no}.&nbsp;</span>
                                                {selectedNoteData?.note}
                                            </span>

                                            {selectedNoteData?.response_building_inspector &&
                                                selectedNoteData?.response_building_inspector?.file_list &&
                                                selectedNoteData?.response_building_inspector?.file_list?.length > 0 && (
                                                    <div className="note_files_area mb-1">
                                                        <div className="checklist_img_container custom">
                                                            {selectedNoteData?.response_building_inspector?.file_list?.map((file, index) => (
                                                                <div
                                                                    className="note_image_container sm response_image_container"
                                                                    key={file?.id}
                                                                >
                                                                    {file?.extension === "pdf" ? (
                                                                        <div
                                                                            className="response_doc_file_container"
                                                                            onClick={() => handleViewDocument(file?.path!)}
                                                                        >
                                                                            <i className="bi bi-file-earmark-pdf"></i>
                                                                        </div>
                                                                    ) : (
                                                                        <ImagePreview
                                                                            src={file?.path!}
                                                                            alt={`sv_report_image_${file?.id}`}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>

                                {/* Response note */}
                                <div className="col-12">
                                    <Textarea
                                        id="arcResponseNote"
                                        label="Response note"
                                        labelHtmlFor="arcResponseNote"
                                        required={true}
                                        value={arcResponseNote}
                                        placeholder="Enter response note"
                                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setArcResponseNote(e.target.value)}
                                        error={arcResponseNoteError}
                                    />
                                </div>

                                {/* Response Files */}
                                <div className="col-12">
                                    <div className="page_form_group">
                                        <label htmlFor="" className="custom_form_label">Files</label>

                                        <div className="checklist_img_container mt-2 mb-1">
                                            {dataState === 'Edit' && (
                                                <React.Fragment>
                                                    {selectedNoteData?.response_architect?.file_list && selectedNoteData?.response_architect?.file_list?.length > 0 && (
                                                        <React.Fragment>
                                                            {selectedNoteData?.response_architect?.file_list?.map((file, index) => (
                                                                <div className="note_image_container response_image_container" key={file?.id}>
                                                                    {responseFileDeleteLoadingId === file?.id ? (
                                                                        <FileActionLoader />
                                                                    ) : (
                                                                        <React.Fragment>
                                                                            {file?.extension === "pdf" ? (
                                                                                <div
                                                                                    className="response_doc_file_container"
                                                                                    onClick={() => handleViewDocument(file?.path!)}
                                                                                >
                                                                                    <i className="bi bi-file-earmark-pdf"></i>
                                                                                </div>
                                                                            ) : (
                                                                                <ImagePreview
                                                                                    src={file?.path!}
                                                                                    alt={`sv_report_image_${file?.id}`}
                                                                                />
                                                                            )}
                                                                        </React.Fragment>
                                                                    )}

                                                                    <button
                                                                        className="img_clear_btn p-ripple"
                                                                        onClick={() => handleDeleteResponseFile(file?.id!)}
                                                                    >
                                                                        <i className="bi bi-x"></i>
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </React.Fragment>
                                                    )}
                                                </React.Fragment>
                                            )}

                                            {arcResponseFiles && arcResponseFiles?.length > 0 && (
                                                <React.Fragment>
                                                    {arcResponseFiles.map((file, index) => {
                                                        const fileExtension = file.name?.split(".").pop()?.toLowerCase();
                                                        return (
                                                            <div
                                                                className="note_image_container response_image_container"
                                                                key={index}
                                                            >
                                                                {fileExtension === "pdf" ? (
                                                                    <div
                                                                        className="response_doc_file_container"
                                                                        onClick={() => handleViewDocument(URL.createObjectURL(file))}
                                                                    >
                                                                        <i className="bi bi-file-earmark-pdf"></i>
                                                                    </div>
                                                                ) : (
                                                                    <ImagePreview
                                                                        src={URL.createObjectURL(file)}
                                                                        alt={`sv_report_image_${index}`}
                                                                    />
                                                                )}

                                                                <button
                                                                    className="img_clear_btn p-ripple"
                                                                    onClick={() => handleRemoveResponseFile(index)}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            )}

                                            <Tooltip target={`.img_upload_label`} />

                                            <label
                                                htmlFor="arcResponseFiles"
                                                className="img_upload_label p-ripple"
                                                data-pr-tooltip="Upload files"
                                                data-pr-position="right"
                                            >
                                                <i className="bi bi-upload"></i>
                                                <input
                                                    id="arcResponseFiles"
                                                    multiple
                                                    type="file"
                                                    accept={documentFileExtension + ', ' + imageFileExtension}
                                                    onChange={handleResponseFilesUpload}
                                                />
                                                <Ripple />
                                            </label>
                                        </div>

                                        <span className="file_upload_info pt-1">
                                            <i className="bi bi-info-circle"></i> Acceptable file format : {documentFileExtension + ', ' + imageFileExtension}
                                        </span>

                                        {arcResponseFilesError &&
                                            <small className="form_error_msg">{arcResponseFilesError}</small>
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Mail modal */}
            <Dialog
                header={mailModalHeader}
                footer={mailModalFooter}
                headerClassName="custom_modal_header"
                visible={showMailModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseMailModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 p-sm-3 pb-sm-0 w-100">
                        <div className="row">
                            <div className="col-12">
                                {emailAddresses.map((email, index) => (
                                    <div className="mail_input_area d-flex align-items-center mb-2" key={index}>
                                        <TextInput
                                            id={`emailAddress-${index}`}
                                            label={`E-mail address ${index + 1}`}
                                            labelHtmlFor={`emailAddress-${index}`}
                                            required={true}
                                            value={email.value}
                                            placeholder="abcd@gmail.com"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleEmailChange(index, e.target.value)
                                            }
                                            error={email.error}
                                            keyFilter={'email'}
                                            formGroupClassName="mb-1"
                                        />
                                        {emailAddresses.length > 1 && (
                                            <i
                                                className="bi bi-dash-circle mail_remove_btn"
                                                title="Remove"
                                                onClick={() => handleRemoveMail(index)}
                                            ></i>
                                        )}
                                    </div>
                                ))}

                                <button className={`add_data_btn p-ripple`} onClick={handleAddMail}>
                                    <i className={`bi bi-plus-circle`}></i>
                                    Add mail
                                    < Ripple />
                                </button>
                            </div>

                            <div className="col-12 mt-3">
                                {siteVisitReportData?.file_download_path !== null && (
                                    <div
                                        className="file_attached_area"
                                        onClick={handleViewReportDocument}
                                    >
                                        <i className="bi bi-paperclip"></i>
                                        <h6>Site vist report.pdf</h6>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showNoteDocViewModal}
                onClose={handleCloseFileViewModal}
                fileUrl={documentFilePath}
            />
            {/*  */}
        </React.Fragment >
    )
}

export default SiteVisitReportDetail;